<template>
  <div>
    <a-modal
      v-model="visible"
      :title="$t('logwork.submit_modal.title')"
      :width="800"
      :maskClosable="false"
      :footer="null"
      @cancel="handleCancel"
    >
      <a-spin :spinning="loading">
        <div class="logwork-submit-modal">
          <!-- Weeks Table -->
          <table class="weeks-table">
            <thead>
              <tr>
                <th>{{ $t("logwork.submit_modal.week") }}</th>
                <th>{{ $t("logwork.submit_modal.status") }}</th>
                <th>{{ $t("logwork.submit_modal.action") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="week in weeks" :key="week.week_number">
                <td>
                  <div class="week-range">
                    {{ formatWeekDate(week.from) }} ~
                    {{ formatWeekDate(week.to) }}
                  </div>
                </td>
                <td>
                  <a-tag :color="getStatusColor(week.status)">
                    {{ $t(`logwork.submit_modal.status.${week.status}`) }}
                  </a-tag>
                </td>
                <td>
                  <a-button
                    v-if="week.status === 'open'"
                    type="primary"
                    size="small"
                    @click="handleSubmitWeek(week)"
                    :loading="submittingWeek === week.week_number"
                  >
                    {{ $t("logwork.submit_modal.submit") }}
                  </a-button>
                  <a-button
                    v-else-if="week.status === 'rejected'"
                    type="primary"
                    size="small"
                    @click="handleSubmitWeek(week)"
                    :loading="submittingWeek === week.week_number"
                  >
                    {{ $t("logwork.submit_modal.resubmit") }}
                  </a-button>
                  <span v-else class="text-muted">-</span>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Empty State -->
          <div v-if="weeks.length === 0" class="empty-state">
            <p>{{ $t("logwork.submit_modal.no_weeks") }}</p>
          </div>

          <!-- Note -->
          <div class="modal-note">
            <p>{{ $t("logwork.submit_modal.description") }}</p>
          </div>

          <!-- Action Buttons -->
          <div class="modal-actions">
            <a-button @click="handleCancel">
              {{ $t("common.cancel") }}
            </a-button>
          </div>
        </div>
      </a-spin>
    </a-modal>

    <!-- Confirmation Modal -->
    <a-modal
      v-model="confirmVisible"
      :title="$t('logwork.submit_modal.confirm_title')"
      :maskClosable="false"
      :footer="null"
      width="500"
      centered
    >
      <div class="submit-confirm-modal">
        <!-- Icon -->
        <div class="confirm-icon">
          <a-icon type="mail" theme="filled" />
        </div>

        <!-- Confirm Message -->
        <div class="confirm-message">
          <p>{{ $t("logwork.submit_modal.confirm_text") }}</p>
          <p class="week-info">
            {{ confirmWeek ? confirmWeek.from : "" }} ~
            {{ confirmWeek ? confirmWeek.to : "" }}
          </p>
        </div>

        <!-- Note Field -->
        <div class="confirm-note">
          <label>{{ $t("logwork.submit_modal.confirm_note_label") }}</label>
          <a-textarea
            v-model="confirmNote"
            :placeholder="$t('logwork.submit_modal.confirm_note_placeholder')"
            :rows="3"
          />
        </div>

        <!-- Action Buttons -->
        <div class="confirm-actions">
          <a-button @click="handleConfirmCancel">
            {{ $t("logwork.submit_modal.confirm_cancel") }}
          </a-button>
          <a-button
            type="primary"
            danger
            @click="handleConfirmSubmit"
            :loading="submittingConfirm"
          >
            {{ $t("logwork.submit_modal.confirm_submit") }}
          </a-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import moment from "moment";
import LogworkService from "../../api/logwork";

const service = LogworkService;

export default {
  name: "LogworkSubmitModal",
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      submittingWeek: null,
      weeks: [],
      error: null,
      confirmVisible: false,
      confirmWeek: null,
      confirmNote: "",
      submittingConfirm: false
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.fetchWeeks();
      }
    }
  },
  methods: {
    fetchWeeks() {
      this.loading = true;
      service
        .getSubmitWeeks()
        .then(response => {
          // Response should contain weeks with format:
          // { week_number: 1, from: "2025-11-24", to: "2025-11-30", status: "open", comment: "" }
          const data = response.data?.data || {};
          this.weeks = Object.entries(data).map(([weekNum, weekData]) => ({
            week_number: parseInt(weekNum),
            ...weekData
          }));
        })
        .catch(error => {
          console.error("Failed to fetch weeks:", error);
          this.$message.error(this.$t("logwork.submit_modal.error_weeks"));
          this.weeks = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatWeekDate(dateStr) {
      return moment(dateStr).format("MM/DD/YYYY");
    },
    getStatusColor(status) {
      const colors = {
        open: "purple",
        submitted: "green",
        approved: "blue",
        rejected: "gray"
      };
      return colors[status] || "default";
    },
    handleSubmitWeek(week) {
      // Show confirmation modal
      this.confirmWeek = week;
      this.confirmNote = "";
      this.confirmVisible = true;
    },
    handleConfirmCancel() {
      this.confirmVisible = false;
      this.confirmWeek = null;
      this.confirmNote = "";
    },
    handleConfirmSubmit() {
      if (this.confirmWeek) {
        this.submitWeek(this.confirmWeek);
      }
    },
    submitWeek(week) {
      this.submittingConfirm = true;
      service
        .submitLogwork({
          week_number: week.week_number
        })
        .then(() => {
          this.$message.success(this.$t("logwork.submit_modal.submit_success"));
          this.$emit("success");
          // Close confirmation modal
          this.confirmVisible = false;
          this.confirmWeek = null;
          this.confirmNote = "";
          // Refresh weeks list
          this.fetchWeeks();
        })
        .catch(error => {
          console.error("Submit failed:", error);
          this.$message.error(this.$t("logwork.submit_modal.submit_error"));
        })
        .finally(() => {
          this.submittingConfirm = false;
        });
    },
    handleCancel() {
      this.close();
    },
    close() {
      this.$emit("update:visible", false);
    }
  }
};
</script>

<style scoped>
.logwork-submit-modal {
  padding: 0;
}

.weeks-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
}

.weeks-table thead {
  background-color: #1890ff;
}

.weeks-table thead th {
  color: white;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  border: 1px solid #1890ff;
}

.weeks-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
}

.weeks-table tbody tr:hover {
  background-color: #fafafa;
}

.weeks-table tbody td {
  padding: 12px;
  border: 1px solid #f0f0f0;
}

.week-range {
  font-weight: 500;
  color: #262626;
}

.text-muted {
  color: #8c8c8c;
}

.empty-state {
  text-align: center;
  padding: 40px 0;
  color: #8c8c8c;
}

.modal-note {
  padding: 12px;
  background-color: #fafafa;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 12px;
  color: #595959;
}

.modal-actions {
  text-align: right;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.modal-actions button {
  margin-left: 8px;
}

.submit-confirm-modal {
  text-align: center;
}

.confirm-icon {
  margin-bottom: 24px;
}

.confirm-icon /deep/ .anticon {
  font-size: 48px;
  color: #e91e63;
}

.confirm-message {
  margin-bottom: 24px;
}

.confirm-message p {
  margin: 0;
  font-size: 14px;
  color: #262626;
}

.confirm-message .week-info {
  font-weight: 600;
  font-size: 16px;
  margin-top: 8px;
  color: #1890ff;
}

.confirm-note {
  margin-bottom: 24px;
  text-align: left;
}

.confirm-note label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #262626;
}

.confirm-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.confirm-actions button {
  min-width: 120px;
}
</style>
